import { isFinishedStatus } from '../../utils/projectStatus';
import { pluralize } from '../../utils/format';
import { awaitingEditorResponse, parseDeadline } from '../EditorWorkspace/workflow';

/**
 * Rules for the editor's project list (/edit-projects): what needs the
 * editor first, the summary line and its filters, and which projects are
 * finished. No React here, so the rules can be tested on their own.
 */

/** A project as the list query (FetchEditorRequests) returns it. */
export interface EditorProject {
  id: string;
  projectName?: string | null;
  status?: string | null;
  videoId?: string | null;
  videoName?: string | null;
  userId?: string | null;
  createdAt?: string | null;
  deadline?: string | null;
  editor?: { id?: string | null; email?: string | null; name?: string | null; status?: string | null } | null;
  tasks?: Array<{ id?: string | null; status?: string | null } | null> | null;
  taskProgress?: { totalTasks?: number | null; completedTasks?: number | null } | null;
  sourceType?: string | null;
  multicamSnapshot?: { name?: string | null; angles?: unknown[] | null } | null;
  /** Not in the list query yet: shown as soon as the API sends it. */
  creatorName?: string | null;
  /** Not in the list query yet: shown as soon as the API sends it. */
  priority?: string | null;
}

const upper = (value?: string | null) => String(value || '').trim().toUpperCase();
const DAY_MS = 24 * 60 * 60 * 1000;
/** "Due this week" is the next seven days, today included. */
export const DUE_SOON_DAYS = 7;

/** The assignment is the editor's to accept or decline (same rule as the accept/decline actions). */
export const isAwaitingResponse = (project: EditorProject): boolean =>
  awaitingEditorResponse(project.status, Boolean(project.editor));

export const hasChangesRequested = (project: EditorProject): boolean => upper(project.status) === 'REVISION_REQUESTED';

/** Delivered or cancelled (or a legacy "completed"): read-only, listed under Delivered. */
export const isClosedProject = (project: EditorProject): boolean =>
  isFinishedStatus(project.status) || upper(project.status) === 'COMPLETED';

/** A cut is with the creator (in review, or approved and waiting to be closed). */
const isWithCreator = (project: EditorProject) => ['DRAFT_READY', 'CLIENT_REVIEW', 'FINAL_APPROVAL'].includes(upper(project.status));

/**
 * The deadline is a signal for the editor only while the next move is
 * theirs: not once the project is closed, and not while a cut is with the
 * creator (the row still shows the date).
 */
const deadlineMatters = (project: EditorProject) => !isClosedProject(project) && !isWithCreator(project);

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

export const isOverdue = (project: EditorProject, now: Date): boolean => {
  if (!deadlineMatters(project)) return false;
  const due = parseDeadline(project.deadline);
  return Boolean(due && due.getTime() < now.getTime());
};

/** Due today or in the next six days, and not overdue yet. */
export const isDueSoon = (project: EditorProject, now: Date): boolean => {
  if (!deadlineMatters(project)) return false;
  const due = parseDeadline(project.deadline);
  if (!due || due.getTime() < now.getTime()) return false;
  const days = Math.round((startOfDay(due) - startOfDay(now)) / DAY_MS);
  return days < DUE_SOON_DAYS;
};

/**
 * Where a project sits in the list: the editor's own move first (an
 * assignment to answer, then requested changes), then work in progress,
 * then cuts with the creator. Closed projects are listed separately.
 */
export const queueRank = (project: EditorProject): number => {
  if (isClosedProject(project)) return 4;
  if (isAwaitingResponse(project)) return 0;
  if (hasChangesRequested(project)) return 1;
  if (isWithCreator(project)) return 3;
  return 2;
};

const deadlineTime = (project: EditorProject) => parseDeadline(project.deadline)?.getTime() ?? Number.POSITIVE_INFINITY;

const createdTime = (project: EditorProject) => {
  const time = project.createdAt ? Date.parse(project.createdAt) : NaN;
  return Number.isNaN(time) ? 0 : time;
};

/** Needs-action first; within a group the nearest deadline, then the newest. */
export const compareQueue = (a: EditorProject, b: EditorProject): number =>
  queueRank(a) - queueRank(b) || deadlineTime(a) - deadlineTime(b) || createdTime(b) - createdTime(a);

const compareNewestFirst = (a: EditorProject, b: EditorProject) => createdTime(b) - createdTime(a);

/** The open projects in working order, and the closed ones newest first. */
export const splitQueue = (projects: EditorProject[]) => {
  const valid = projects.filter((project): project is EditorProject => Boolean(project && project.id));
  return {
    active: valid.filter((project) => !isClosedProject(project)).sort(compareQueue),
    closed: valid.filter(isClosedProject).sort(compareNewestFirst),
  };
};

// ---- Summary line and filters -------------------------------------------------

export type QueueFilter = 'awaiting' | 'changes' | 'overdue' | 'due-soon';

export const QUEUE_FILTERS: Array<{ value: QueueFilter; label: (count: number) => string; empty: string }> = [
  { value: 'awaiting', label: (count) => `${count} awaiting response`, empty: 'Nothing is awaiting your response' },
  { value: 'changes', label: (count) => `${count} changes requested`, empty: 'No projects have changes requested' },
  { value: 'overdue', label: (count) => `${count} overdue`, empty: 'Nothing is overdue' },
  { value: 'due-soon', label: (count) => `${count} due this week`, empty: 'Nothing is due this week' },
];

/** The filter named in the URL (?show=awaiting), or none. */
export const parseQueueFilter = (value: string | null | undefined): QueueFilter | null =>
  QUEUE_FILTERS.some((filter) => filter.value === value) ? (value as QueueFilter) : null;

export const matchesQueueFilter = (project: EditorProject, filter: QueueFilter, now: Date): boolean => {
  switch (filter) {
    case 'awaiting':
      return !isClosedProject(project) && isAwaitingResponse(project);
    case 'changes':
      return hasChangesRequested(project);
    case 'overdue':
      return isOverdue(project, now);
    case 'due-soon':
      return isDueSoon(project, now);
    default:
      return true;
  }
};

export const countQueue = (projects: EditorProject[], now: Date): Record<QueueFilter, number> => {
  const counts: Record<QueueFilter, number> = { awaiting: 0, changes: 0, overdue: 0, 'due-soon': 0 };
  projects.forEach((project) => {
    if (!project) return;
    QUEUE_FILTERS.forEach(({ value }) => {
      if (matchesQueueFilter(project, value, now)) counts[value] += 1;
    });
  });
  return counts;
};

// ---- Row details ------------------------------------------------------------------

export type DueTone = 'neutral' | 'attention' | 'problem';

/**
 * How soon an open project is due: "overdue by 2 days", "today", "tomorrow",
 * "in 3 days". Null when there is no valid deadline, when it is more than a
 * week out (the date says enough), or when the deadline no longer matters.
 */
export const describeDue = (project: EditorProject, now: Date): { text: string; tone: DueTone } | null => {
  if (!deadlineMatters(project)) return null;
  const due = parseDeadline(project.deadline);
  if (!due) return null;
  const days = Math.round((startOfDay(due) - startOfDay(now)) / DAY_MS);
  if (due.getTime() < now.getTime()) {
    const late = Math.max(1, -days);
    return { text: days === 0 ? 'overdue' : `overdue by ${pluralize(late, 'day')}`, tone: 'problem' };
  }
  if (days === 0) return { text: 'today', tone: 'attention' };
  if (days === 1) return { text: 'tomorrow', tone: 'attention' };
  if (days < DUE_SOON_DAYS) return { text: `in ${days} days`, tone: 'neutral' };
  return null;
};

/** "Panel discussion · 3 angles" for a multicam set, else the video's name. */
export const projectSource = (project: EditorProject): string | null => {
  if (upper(project.sourceType) === 'MULTICAM' && project.multicamSnapshot) {
    const angles = Array.isArray(project.multicamSnapshot.angles) ? project.multicamSnapshot.angles.length : 0;
    return [project.multicamSnapshot.name || 'Multicam set', pluralize(angles, 'angle')].join(' · ');
  }
  return project.videoName?.trim() || null;
};

/** Tasks done out of all tasks, from the server's summary when it sent one. */
export const taskCounts = (project: EditorProject): { done: number; total: number } => {
  const tasks = (project.tasks || []).filter(Boolean) as Array<{ status?: string | null }>;
  const total = Math.max(tasks.length, Number(project.taskProgress?.totalTasks) || 0);
  const fromTasks = tasks.filter((task) => upper(task.status) === 'COMPLETED').length;
  const done = typeof project.taskProgress?.completedTasks === 'number' ? project.taskProgress.completedTasks : fromTasks;
  return { done: Math.min(done, total), total };
};
